import React, { useCallback } from 'react';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { ProductSwitcherModalList } from '@/feature/product-switcher/ProductSwitcherModalList';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { logger } from '@/utils/logging';
import { runAfterInteractions } from '@/utils/runAfterInteractions';

import { useAvailableProducts } from './use-available-products';
import { Product } from './use-jurisdiction';

type SwitcherOptions = {
    title?: string;
    additionalDescription?: string;
    customCallback?: (product: Product) => void;
};

export const useProductSwitchModal = () => {
    const { showInfoSheet } = useAlerts();

    const { availableProducts } = useAvailableProducts();

    const switchingAvailable = availableProducts.length > 1;

    // function that only shows the product switch modal if switching is available
    const showSwitcher = useCallback(
        ({ title, additionalDescription, customCallback }: SwitcherOptions = {}) => {
            if (switchingAvailable) {
                showInfoSheet({
                    title: title ?? 'Gameplay',
                    description: (
                        <ProductSwitcherModalList
                            additionalDescription={additionalDescription}
                            customCallback={customCallback}
                        />
                    ),
                });
            } else {
                logger.warn('Trying to show the ProductSwitcher with only one available product', availableProducts);
            }
        },
        [availableProducts, showInfoSheet, switchingAvailable]
    );

    // function that shows the product switch modal if switching is available
    // or directly triggers the transition to the only available product
    const handleSwitchNavigation = useCallback(
        (options: SwitcherOptions = {}) => {
            if (switchingAvailable) {
                showSwitcher(options);
            } else {
                const availableProduct = availableProducts[0] || Product.Pickem;
                if (options.customCallback) {
                    options.customCallback(availableProduct);
                    return;
                }
                useProductTransitionStore.getState().actions.triggerTransition(availableProduct);
            }
        },
        [availableProducts, showSwitcher, switchingAvailable]
    );

    const changeProduct = useCallback((product: Product) => {
        runAfterInteractions(() => {
            const actualProduct = product !== Product.None ? product : Product.Pickem;
            useProductTransitionStore.getState().actions.triggerTransition(actualProduct);
        });
    }, []);

    return { availableProducts, switchingAvailable, handleSwitchNavigation, showSwitcher, changeProduct };
};
